import { FunctionComponent, useEffect, useState } from 'react'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddAvatar2 from './AddAvatar2'
import { observer } from 'mobx-react'
//@ts-ignore
import styled, { useTheme } from 'styled-components'
import { BackendUser, OnboardingQuestion, QuestionAnswer } from 'common/types'
import { Button } from 'common/atoms/Button/Button'
import { StyledContainer, StyledSelect, StyledTextField, UpdateButtonContainer } from 'pages/profile/styled'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import { age, cities, countries, gender, provinces } from 'common/select-options'
import { InputFieldGroup } from 'common/molecules/InputFieldGroup/InputFieldGroup'
import { onboardingStoreState } from 'models/onboarding/onboarding.store'
import { useEditProfile } from 'pages/profile/hooks/useEditProfile'
import { Switch } from 'common/atoms/Switch/Switch'
import { SwitchDesignModes } from 'common/atoms/Switch/Switch.types'

const StyledWrapper = styled(Box)`
  margin-top: 21px;
  margin-bottom: 21px;
`

const StyledSwitchWrapper = styled(Box)`
  display: flex;
  width: 100%;
  height: 32px;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`

const StyledLocationText = styled(Typography)`
  color: var(--Accent, #aaa);
  text-align: center;
`

const StyledAvatarWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 156px;
`
const StyledCover = styled(Box)`
  width: 100%;
  height: 116px;
  flex-shrink: 0;
  border-radius: 12px;
  background: ${props => props.theme.colors.supplementary1};
`
const StyledAvatar = styled(Box)`
  position: relative;
  z-index: 1;
  bottom: 3.5rem;
`

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
    width: '100%',
    height: `${props => (props.isDevice ? 'auto' : '709px')}`,
    flexShrink: 0
  }
}))

type Country = {
  country: string
  province: string
  city: string
}

interface EditProfileBioProps {
  user: BackendUser
  setProfileQuestions: Function
}

const EditProfileBio: FunctionComponent<EditProfileBioProps> = ({ user }) => {
  const theme = useTheme()
  const classes = useStyles()
  const { isDevice } = useScreenSizes()
  const { updateButtonEnabled, setUpdateButtonEnabled, isUSOrCanada, setIsUSOrCanada } = onboardingStoreState

  const { completeEditOnboarding, updatedQuestionAnswers } = useEditProfile()

  const [newProfileData, setNewProfileData] = useState<QuestionAnswer[]>(user?.onboardingQuestions)
  const findAnswer = question => {
    const answer = newProfileData.find(ans => ans.question.trim() === question)
    return answer?.answer
  }

  const [location, setLocation] = useState<Country>()
  const handleInputChange = (e, question) => {
    e.preventDefault()
    const { name, value } = e.target
    setUpdateButtonEnabled(true)
    if (name === 'country' || name === 'province' || name === 'city') {
      const updatedLocation = {
        ...location,
        [name]: value
      }
      const concatenatedString = `${updatedLocation?.country}, ${updatedLocation?.province}, ${updatedLocation?.city}`
      setLocation(updatedLocation)

      setNewProfileData(prevData => {
        const updatedData = prevData.map(item => {
          if (item.question === question) {
            return { question: question, answer: concatenatedString }
          } else {
            return item
          }
        })
        return updatedData
      })
    } else {
      setNewProfileData(prevData => {
        const updatedData = prevData.map(item => {
          if (item.question === question) {
            return { question: question, answer: value }
          } else {
            return item
          }
        })
        return updatedData
      })
    }
  }

  const handleSelect = (index: number) => {
    if (index === 0) {
      location.province = ''
      location.city = ''
    }
    if (index === 1 && isUSOrCanada) {
      location.city = ''
    }
  }

  useEffect(() => {
    const requireFields =
      findAnswer('First Name')?.length !== 0 &&
      findAnswer('Last Name')?.length !== 0 &&
      findAnswer('Age Range')?.length !== 0 &&
      findAnswer('Age Range') !== undefined &&
      findAnswer('Age Range') !== age[0]?.label &&
      location?.country?.length !== 0 &&
      location?.country?.trim() !== countries[0]?.label &&
      location?.province?.length !== 0 &&
      location?.city?.trim()?.length !== 0 &&
      location?.city !== undefined
    setUpdateButtonEnabled(requireFields)
  }, [newProfileData])

  useEffect(() => {
    setLocation({
      country: findAnswer('Location')?.split(',')[0],
      province: findAnswer('Location')?.split(',')[1],
      city: findAnswer('Location')?.split(',')[2]
    })
  }, [])

  useEffect(() => {
    setIsUSOrCanada(location?.country === 'United States' || location?.country === 'Canada')
  }, [location?.country])

  return (
    <StyledContainer width="100%" zIndex={0} className={classes.root} isDevice={isDevice}>
      <StyledAvatarWrapper>
        <StyledCover />
        <StyledAvatar>
          <AddAvatar2 user={user} setNewProfileData={setNewProfileData} />
        </StyledAvatar>
      </StyledAvatarWrapper>
      <StyledWrapper>
        <Box
          display="grid"
          minWidth="0"
          gridTemplateColumns={isDevice ? '1fr' : '1fr 1fr'}
          gridGap="1rem"
          width={'100%'}
        >
          <Box minWidth="0">
            <InputFieldGroup
              width="100%"
              label="First Name"
              value={findAnswer('First Name')}
              required
              onChange={e => handleInputChange(e, 'First Name')}
            />
          </Box>
          <Box minWidth="0">
            <InputFieldGroup
              width="100%"
              label="Last Name"
              value={findAnswer('Last Name')}
              required
              onChange={e => handleInputChange(e, 'Last Name')}
            />
          </Box>
        </Box>
        <Box
          display="grid"
          minWidth="0"
          gridTemplateColumns={isDevice ? '1fr' : '1fr 1fr'}
          gridGap="1rem"
          width={'100%'}
          style={{ marginTop: isDevice ? '0.8em' : '2.47em' }}
        >
          <Box minWidth="0">
            <StyledSelect
              defaultValue={''}
              width="100%"
              label="Gender Identity (Internal use only)"
              menuItemList={gender}
              onChange={e => {
                handleInputChange(e, 'Gender Identity')
              }}
              theme={theme}
              placeholder="Select Gender"
              value={findAnswer('Gender Identity')}
              multiple={false}
              native={false}
              key={findAnswer('Gender Identity')}
            />
          </Box>
          <Box minWidth="0">
            <StyledSelect
              label="Age Range"
              required
              width="100%"
              menuItemList={age.map(val => ({ ...val, value: val?.label }))}
              onChange={e => {
                handleInputChange(e, 'Age Range')
              }}
              theme={theme}
              placeholder="Select Age Range"
              value={findAnswer('Age Range')}
              multiple={false}
              native={false}
              key={findAnswer('Age Range')}
            />
          </Box>
        </Box>
        <Box
          display="grid"
          minWidth="0"
          gridTemplateColumns={isDevice ? '1fr' : '1fr 1fr 1fr'}
          gridGap="1rem"
          width={'100%'}
          style={{ marginTop: isDevice ? '0.8em' : '2.47em' }}
        >
          <Box minWidth="0">
            <StyledSelect
              label="Country"
              name="country"
              required
              width="100%"
              menuItemList={countries.map(val => ({ ...val, value: val?.label }))}
              onChange={e => {
                handleSelect(0)
                handleInputChange(e, 'Location')
              }}
              theme={theme}
              placeholder="Select Country"
              value={location?.country?.trim()}
              multiple={false}
              native={false}
              key={location?.country?.trim()}
            />
          </Box>
          <Box minWidth="0">
            {!isUSOrCanada && (
              <StyledTextField
                name="province"
                width="100%"
                placeholder="Enter Province / State"
                label="Province / State"
                value={location?.province}
                onChange={e => handleInputChange(e, 'Location')}
                required
                disabled={location?.country === ''}
              />
            )}
            {isUSOrCanada && (
              <StyledSelect
                label="Province / State"
                name="province"
                required
                width="100%"
                menuItemList={provinces
                  ?.find(province => province.country === location?.country)
                  ?.states.map(val => ({ ...val, value: val?.label }))}
                onChange={e => {
                  handleSelect(1)
                  handleInputChange(e, 'Location')
                }}
                theme={theme}
                placeholder="Enter Province / State"
                value={location?.province?.trim()}
                multiple={false}
                native={false}
                key={location?.province?.trim()}
              />
            )}
          </Box>
          <Box minWidth="0">
            {!isUSOrCanada && (
              <StyledTextField
                name="city"
                width="100%"
                placeholder="Enter City"
                label="City"
                value={location?.city}
                onChange={e => handleInputChange(e, 'Location')}
                required
                disabled={location?.country === ''}
              />
            )}
            {isUSOrCanada && (
              <StyledSelect
                label="City"
                name="city"
                required
                width="100%"
                menuItemList={cities
                  ?.find(val => val.state === location?.province?.trim() && val.country === location?.country?.trim())
                  ?.cities.map(val => ({ ...val, value: val?.label }))}
                onChange={e => {
                  handleSelect(2)
                  handleInputChange(e, 'Location')
                }}
                theme={theme}
                placeholder="Enter City"
                value={location?.city?.trim()}
                multiple={false}
                native={false}
                key={location?.city?.trim()}
              />
            )}
          </Box>
        </Box>
      </StyledWrapper>
      <StyledSwitchWrapper>
        <StyledLocationText variant="subtitle2">Location</StyledLocationText>
        <Switch design={SwitchDesignModes.LOCATION_SWITCH} />
      </StyledSwitchWrapper>
    </StyledContainer>
  )
}

export default observer(EditProfileBio)
